// Data-source seam for the Options Flow page — now live over the WebSocket.
//
// This is the whole swap the seam was built for: the provider, dispatch, and
// every renderer are UNCHANGED — only this hook's body moved from "import the
// fixture" to "subscribe to the WS". The return shape (`FlowFeedResult`) is
// stable.
//
// Contract (TEST-live): method `flow_metrics`, params `{ token_pair }` — a
// fixed nominal 'BTC_USD' the transport REQUIRES (it errors without it); the
// value is ignored, the response is the whole nested bundle. The transport
// serves ONE nested `_all.json` — `{ flow_metrics: { default_venue, venues[],
// data: { <venue>: { <token>: <window-bundle> } } } }` — carrying EVERY
// venue/token/window in a single snapshot. All three dimensions switch
// CLIENT-SIDE in the provider (it indexes data[venue][token].windows[window]);
// there is no per-token or per-venue round-trip. We unwrap `.flow_metrics` and
// hand the inner object to the provider, which resolves the nested bundle. It's
// a streaming subscription (full snapshot every push, ~5 min): the handler fires
// repeatedly and we just replace state (idempotent).
//
// A legacy payload (no `data`/`venues` — the older single-token window bundle,
// or a bare single-envelope) still renders: the provider treats it as
// deribit/BTC. So the swap is backward compatible.
//
// Dev/offline: set `VITE_OPTIONS_FLOW_FIXTURE=true` to serve the bundled
// snapshot instead of the socket (storybook / offline dev). The LIVE path never
// shows the stale fixture — it stays in `loading` until the first real snapshot.

import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';

import { FlowFeedResult, FlowPayload } from './types';
import { listReports } from './options-flow.helpers';
import sampleAll from './sample_all.json';

// Dev-only offline fallback flag (Vite exposes VITE_-prefixed env).
const USE_FIXTURE = import.meta.env?.VITE_OPTIONS_FLOW_FIXTURE === 'true';

// Fixed channel id for the single subscription. MUST NOT contain any
// SUBSCRIPTIONS_KEY substring (Diletta WS id-routing gotcha — a one-shot id
// colliding with a subscription key is mis-routed). Verified clean against
// constants/websocket-events.ts.
const CHANNEL_ID = 'FLOW_METRICS_BTC_USD';

// The transport REQUIRES a `token_pair` param and errors without it, so we send
// a fixed nominal. It's NOT tied to the active token — the response carries the
// whole nested `_all.json` (every venue/token/window) regardless of the value,
// and the provider indexes data[venue][token] client-side.
const NOMINAL_TOKEN_PAIR = 'BTC_USD';

// Auto re-request cadence — 1 HOUR (was 5 min). Dropped to cut AWS-gateway
// egress: the backend re-reads the full ~48MB `_all.json` off the AWS Q: gateway
// per request (no cache), so aggregate cross-region reads scale with poll
// frequency x open pages. Flow analytics is slow-moving (hourly bars), so hourly
// auto-refresh loses no meaningful freshness, and the manual `refresh()` (wired
// to a Refresh button on the page) covers anyone who wants it sooner. The full
// fix is a backend in-region cache keyed on `meta.generated_utc` — tracked
// separately. Re-SEND (no re-subscribe): the callback replaces the snapshot on
// each push, so the last snapshot stays visible until the next lands.
const REFRESH_MS = 60 * 60 * 1000;

// The `flow_metrics` request frame — identical for the initial send and every
// scheduled refresh, so both routes hit the same subscription callback.
const FLOW_METRICS_REQUEST = JSON.stringify({
  jsonrpc: '2.0',
  id: CHANNEL_ID,
  method: 'flow_metrics',
  params: { token_pair: NOMINAL_TOKEN_PAIR },
});

// Tolerant unwrap of the topic-wrapped response. The payload can arrive as
// `{ result: { flow_metrics: {...} } }` (the contract), or — depending on how
// the transport frames it — `{ flow_metrics: {...} }`, or already-unwrapped.
// Fall through those shapes; return null for anything that isn't an object
// (e.g. a plain "ok" ack), so acks never clobber a real snapshot.
export const unwrapFlowMetrics = (resp: unknown): FlowPayload | null => {
  const r = resp as Record<string, unknown> | null | undefined;
  const result = r?.result as Record<string, unknown> | undefined;
  const topic = result?.flow_metrics ?? r?.flow_metrics ?? r?.result ?? r;
  if (!topic || typeof topic !== 'object') return null;
  return topic as FlowPayload;
};

// A snapshot is only worth rendering if it carries a nested `data` bundle, a
// `windows` bundle, at least one report entry, or the payload-wide `_meta`. This
// is the ack/heartbeat guard: a subscription ack that unwraps to a bare
// `{ status: 'subscribed' }` has none of these, so we leave state untouched
// (stay in `loading`) rather than blank the page.
const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const hasFlowContent = (payload: FlowPayload): boolean => {
  const p = payload as { data?: unknown; windows?: unknown };
  return isObj(p.data) || isObj(p.windows) || listReports(payload).length > 0 || '_meta' in payload;
};

// ONE subscription returns the whole nested bundle (every venue/token/window);
// venue/token/window switching is all client-side in the provider, so nothing
// here keys on the active selection. The channel id is a fixed prefix — still
// clear of every SUBSCRIPTIONS_KEY substring (WS id-routing gotcha).
export const useOptionsFlowFeed = (): FlowFeedResult & {
  refresh: () => void;
  lastUpdated: number | null;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [result, setResult] = useState<FlowFeedResult>({ payload: null, status: 'loading' });
  // Wall-clock (ms) of the last applied snapshot — drives the "refreshed HH:MM"
  // stamp next to the Refresh button (updates on manual refresh + hourly poll).
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const keyRef = useRef<string | null>(null);

  // The WebSocketProvider re-creates these functions on every render (they're
  // not memoized upstream). Holding them in a ref keeps the subscribe effect
  // from re-running — and tearing down + re-subscribing — on every provider
  // render. Without this the effect churned, blanking the payload each time.
  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };

  useEffect(() => {
    // Dev/offline: serve the bundled snapshot, no socket.
    if (USE_FIXTURE) {
      setResult({ payload: sampleAll as unknown as FlowPayload, status: 'ready' });
      return;
    }

    // Always go through the live `wsRef.current` (never a snapshot taken at
    // effect-run time) so the initial send, the subscribe, and the interval all
    // use the same current WS-context functions.

    // Tear down any prior subscription before (re)subscribing on reconnect.
    if (keyRef.current) {
      wsRef.current.unsubscribeFromChannel(keyRef.current);
      keyRef.current = null;
    }

    // Show `loading` ONLY when there's genuinely no snapshot yet. A re-subscribe
    // (reconnect / provider churn) keeps the last snapshot on screen — the next
    // push just replaces it — so the page never flashes blank mid-session.
    setResult((prev) => (prev.payload ? prev : { payload: null, status: 'loading' }));

    if (!isConnected) return;

    keyRef.current = CHANNEL_ID;

    wsRef.current.sendMessage(FLOW_METRICS_REQUEST);

    // Hourly bars, 5-min re-request: re-SEND the same request on a fixed timer so
    // a long-open tab keeps rebuilding (the current-hour bar updates intra-hour).
    // We do NOT re-subscribe — the callback below already replaces the snapshot
    // on each push, and re-sending keeps the last snapshot visible until then.
    const refreshTimer = setInterval(() => {
      wsRef.current.sendMessage(FLOW_METRICS_REQUEST);
    }, REFRESH_MS);

    wsRef.current.subscribeToChannel(CHANNEL_ID, (resp: unknown) => {
      const err = (resp as { error?: unknown })?.error;
      if (err) {
        // Surface the transport error body so the next failure is diagnosable
        // rather than a bare "could not load".
        console.warn('[options-flow] flow_metrics error', err);
        // Only surface the error state before we have data; once a snapshot is
        // up, keep showing it rather than blanking on a transient error frame.
        setResult((prev) => (prev.payload ? prev : { payload: null, status: 'error' }));
        return;
      }
      const payload = unwrapFlowMetrics(resp);
      if (payload && hasFlowContent(payload)) {
        setResult({ payload, status: 'ready' });
        setLastUpdated(Date.now());
      }
    });

    return () => {
      clearInterval(refreshTimer);
      if (keyRef.current) {
        wsRef.current.unsubscribeFromChannel(keyRef.current);
        keyRef.current = null;
      }
    };
    // Depends on connection state (+ the build-time fixture flag) only — NOT on
    // the WS-context function identities (held in wsRef), so it re-subscribes on
    // real connection changes, not on every provider render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Manual refresh — re-send the same request immediately (same path as the
  // hourly timer), so users can force fresh data between the (now hourly) auto
  // polls. wsRef is stable, so [] deps.
  const refresh = useCallback(() => {
    if (USE_FIXTURE) return;
    wsRef.current.sendMessage(FLOW_METRICS_REQUEST);
  }, []);

  return { ...result, refresh, lastUpdated };
};
